import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import classNames from 'classnames';
import { Modal } from 'react-bootstrap';

import FloatingInput from 'components/elements/atoms/FloatingInput/FloatingInput';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';

import * as Actions from '../../../redux/actions/index';
import { Icon, ICONS } from '../../Icons';

import './ManageCourse.scss';
import { FocusTrap } from 'focus-trap-react';

const SUBJECT_OPTIONS = [
  'Business',
  'Civics',
  'Economics',
  'English',
  'Government',
  'History',
  'Other',
];

const STEPS = ['Basics', 'Description', 'Subjects', 'News suggestions'];

const EDIT_STEPS = ['Basics', 'Description', 'Subjects'];

function getInitialSubjectSelection(course) {
  if (!course.subject) {
    return {
      selectedSubject: '',
      customSubject: '',
    };
  }

  if (SUBJECT_OPTIONS.includes(course.subject)) {
    return {
      selectedSubject: course.subject,
      customSubject: '',
    };
  }

  return {
    selectedSubject: 'Other',
    customSubject: course.subject,
  };
}

function ManageCourse({ onClose, actions, course = {} }) {
  const isCreateMode = !course.name;
  const steps = isCreateMode ? STEPS : EDIT_STEPS;
  const initialSubjectState = useMemo(
    () => getInitialSubjectSelection(course),
    [course],
  );

  const [step, setStep] = useState(0);
  const [name, setName] = useState(course.name || '');
  const [startDate, setStartDate] = useState(
    () => course.startDate || new Date().toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState(() => course.endDate || '');
  const [selectedSubject, setSelectedSubject] = useState(
    initialSubjectState.selectedSubject,
  );
  const [customSubject, setCustomSubject] = useState(
    initialSubjectState.customSubject,
  );
  const [description, setDescription] = useState(course.description || '');
  const [wantsNewsSuggestions, setWantsNewsSuggestions] = useState(() =>
    typeof course.newsSuggestionSettings?.enabled === 'boolean'
      ? course.newsSuggestionSettings.enabled
      : null,
  );
  const [reviewFirst, setReviewFirst] = useState(() =>
    typeof course.newsSuggestionSettings?.autoPublishWithoutReview === 'boolean'
      ? !course.newsSuggestionSettings.autoPublishWithoutReview
      : undefined,
  );

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdCourse, setCreatedCourse] = useState(null);
  const [postCreateScreen, setPostCreateScreen] = useState('');
  const modalContentRef = useRef(null);

  const hasDataChanged =
    isCreateMode ||
    (() => {
      const initialNormalizedSubject =
        initialSubjectState.selectedSubject === 'Other'
          ? initialSubjectState.customSubject.trim()
          : initialSubjectState.selectedSubject;
      const currentNormalizedSubject =
        selectedSubject === 'Other' ? customSubject.trim() : selectedSubject;
      return (
        name.trim() !== (course.name || '') ||
        startDate !==
          (course.startDate || new Date().toISOString().split('T')[0]) ||
        endDate !== (course.endDate || '') ||
        description.trim() !== (course.description || '') ||
        currentNormalizedSubject !== initialNormalizedSubject
      );
    })();

  const normalizedSubject =
    selectedSubject === 'Other' ? customSubject.trim() : selectedSubject;
  const hasInvalidDates =
    !!startDate && !!endDate && moment(startDate).isAfter(moment(endDate));

  const clearError = () => {
    if (error) {
      setError('');
    }
  };

  const moveFocusOffInput = (event) => {
    const interactiveElement = event.target.closest(
      'input, textarea, button, a, select, label',
    );

    if (interactiveElement) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      modalContentRef.current?.focus();
    });
  };

  const closeDatePickerAfterSelection = (event, setValue) => {
    clearError();
    setValue(event.target.value);

    window.requestAnimationFrame(() => {
      event.target.blur();
    });
  };

  const getStepError = () => {
    if (step === 0) {
      if (!name.trim() || !startDate || !endDate) {
        return 'Please complete the basic course details.';
      }

      if (hasInvalidDates) {
        return 'End date must be on or after the start date.';
      }
    }

    if (step === 2 && !normalizedSubject) {
      return 'Please select a subject.';
    }

    if (step === 3) {
      if (wantsNewsSuggestions === null) {
        return 'Please select an option.';
      }
      if (wantsNewsSuggestions && reviewFirst === undefined) {
        return 'Please select an option.';
      }
    }

    return '';
  };

  const isCurrentStepValid = () => !getStepError();

  const openTopicSetupFlow = () => {
    const courseId = createdCourse?.courseId || createdCourse?._id;

    if (!courseId) {
      return;
    }

    onClose();
    actions.openModal('syllabus-upload', { courseId, mode: 'add' });
  };

  const handleCourseCreated = (savedCourse) => {
    const courseId = savedCourse?.courseId || savedCourse?._id;

    if (courseId) {
      actions.selectClassroomEntity({
        selectedCourse: courseId,
        selectedCourseTab: 'settings_admin',
        selectedTopic: null,
        selectedStudent: null,
        selectedGroupStudent: null,
        selectedReading: null,
        selectedReadingGrades: null,
      });
      saveSyllabusSettings(courseId);
    }

    setCreatedCourse(savedCourse);
  };

  const saveCourse = async () => {
    const stepError = getStepError();

    if (stepError) {
      setError(stepError);
      return;
    }

    setSaving(true);

    const payload = {
      courseId: course.courseId,
      name: name.trim(),
      subject: normalizedSubject,
      description: description.trim(),
      startDate: startDate || null,
      endDate: endDate || null,
      isFavorite: course.isFavorite,
      ...(isCreateMode
        ? {
            readingOptions: wantsNewsSuggestions
              ? ['readings_in_wild_course_topics']
              : ['readings_in_wild'],
            autoPublishWithoutReview: wantsNewsSuggestions
              ? !reviewFirst
              : undefined,
          }
        : {}),
    };

    try {
      if (isCreateMode) {
        const savedCourse = await actions.createCourse(payload);
        handleCourseCreated(savedCourse);
      } else {
        await actions.updateCourse(payload);
        const updatedCourse = { ...course, ...payload };
        setCreatedCourse(updatedCourse);
        setPostCreateScreen('success');
      }
    } catch (requestError) {
      setError(
        requestError.message ||
          `Error occurred while ${
            isCreateMode ? 'adding' : 'updating'
          } course. Please try again.`,
      );
    } finally {
      setSaving(false);
    }
  };

  const goToNextStep = () => {
    const stepError = getStepError();

    if (stepError) {
      setError(stepError);
      return;
    }

    setStep((current) => current + 1);
  };

  const saveSyllabusSettings = async (courseId) => {
    setSaving(true);
    try {
      await Promise.all([
        actions.updateSyllabusSettings(courseId, {
          autoPublishWithoutReview: !reviewFirst,
        }),
        actions.updateSyllabusStatus(courseId, wantsNewsSuggestions),
      ]);

      setPostCreateScreen('success');
    } catch (requestError) {
      setError(
        requestError.message || 'Error saving sources. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryAction = () => {
    if ((isCreateMode && step === 3) || (!isCreateMode && step === 2)) {
      saveCourse();
      return;
    }

    goToNextStep();
  };

  const goBack = () => {
    if (step === 0) {
      return;
    }

    setStep((current) => current - 1);
  };

  const renderHeader = () => (
    <div className="header-row">
      <h2 className="modal-create-title">
        {!isShowingPostCreateScreen
          ? isCreateMode
            ? 'Create a course'
            : 'Update your course basics'
          : ''}
      </h2>
      <button
        type="button"
        onClick={onClose}
        className="btn-av-container close btn-close"
        aria-label="Close"
      >
        <Icon icon={ICONS.CLOSE} className="mr-0" />
      </button>
    </div>
  );

  const renderSteps = () => (
    <div className="steps-container">
      {steps.map((stepName, index) => {
        const isCompleted = index < step;
        const isActive = index === step;

        return (
          <div
            key={stepName}
            className={classNames('step-item', {
              completed: isCompleted,
              active: isActive,
            })}
          >
            <div className="step-circle">
              {isCompleted ? (
                <Icon icon={ICONS.VERIFIED} className="step-check" />
              ) : (
                index + 1
              )}
            </div>
            <div className="step-label">{stepName}</div>
          </div>
        );
      })}
    </div>
  );

  const renderBasicsStep = () => (
    <div className="step-panel">
      {/* <h2>{isCreateMode ? 'Create course' : 'Update your course'}</h2> */}
      <p className="mb-4">Let&apos;s start with the basics.</p>
      <FloatingInput
        autoFocus
        value={name}
        labelName="Course name"
        onChange={(e) => {
          clearError();
          setName(e.target.value);
        }}
        required
      />
      <div className="date-grid">
        <div>
          <FloatingInput
            value={startDate ? moment(startDate).format('YYYY-MM-DD') : ''}
            type="date"
            labelName="Start date"
            onChange={(e) => closeDatePickerAfterSelection(e, setStartDate)}
            max={endDate ? moment(endDate).format('YYYY-MM-DD') : undefined}
            required
          />
        </div>
        <div>
          <FloatingInput
            value={endDate ? moment(endDate).format('YYYY-MM-DD') : ''}
            type="date"
            labelName="End date"
            onChange={(e) => closeDatePickerAfterSelection(e, setEndDate)}
            min={startDate ? moment(startDate).format('YYYY-MM-DD') : undefined}
            required
          />
        </div>
      </div>
      {hasInvalidDates ? (
        <div className="step-error">
          End date must be on or after the start date.
        </div>
      ) : null}
      {step === 0 && error && !hasInvalidDates ? (
        <div className="step-error">{error}</div>
      ) : null}
    </div>
  );

  const renderSubjectStep = () => (
    <div className="step-panel">
      <h2>What subject is this course?</h2>
      <p>
        This helps us surface the most relevant news articles for your students.
      </p>
      <div className="pill-grid">
        {SUBJECT_OPTIONS.map((option) => (
          <RectangularButton
            key={option}
            className={classNames('subject-pill', {
              selected: selectedSubject === option,
            })}
            onClick={() => {
              clearError();
              setSelectedSubject(option);
            }}
          >
            {option}
          </RectangularButton>
        ))}
      </div>
      {selectedSubject === 'Other' && (
        <div className="other-subject-input">
          <FloatingInput
            autoFocus
            value={customSubject}
            labelName="Subject"
            onChange={(e) => {
              clearError();
              setCustomSubject(e.target.value);
            }}
          />
        </div>
      )}
      {step === 2 && error ? <div className="step-error">{error}</div> : null}
    </div>
  );

  const renderDescriptionStep = () => (
    <div className="step-panel">
      {/* <h2>Describe your course</h2> */}
      <p className="mb-4">
        A short description helps us match articles to your specific curriculum.
        (optional)
      </p>
      {/* <div className="textarea-label">
        Course description <span>(optional)</span>
      </div> */}
      <textarea
        className="course-description"
        placeholder="e.g. An introductory survey of American economic history from the colonial era to the present, with emphasis on industrialization and globalization. (optional)"
        value={description}
        onChange={(e) => {
          clearError();
          setDescription(e.target.value);
        }}
        rows={6}
      />
    </div>
  );

  const renderYesNo = (value, onChange) => (
    <div className="news-yes-no">
      <button
        type="button"
        className={classNames('btn-av-container news-yes-no-option', {
          selected: value === true,
        })}
        onClick={() => onChange(true)}
      >
        <div className="topic-option-radio">
          <span className="topic-option-radio-inner" />
        </div>
        <span>Yes</span>
      </button>
      <button
        type="button"
        className={classNames('btn-av-container news-yes-no-option', {
          selected: value === false,
        })}
        onClick={() => onChange(false)}
      >
        <div className="topic-option-radio">
          <span className="topic-option-radio-inner" />
        </div>
        <span>No</span>
      </button>
    </div>
  );

  const renderReadingStep = () => (
    <div className="step-panel">
      <div className="news-suggestion-options">
        <div className="news-suggestion-question">
          <p className="mb-3">
            Should we send you weekly news articles related to your class?
          </p>
          {renderYesNo(wantsNewsSuggestions, setWantsNewsSuggestions)}
        </div>
        {wantsNewsSuggestions && (
          <>
            <div className="news-suggestion-question sub-question mt-2">
              <p className="mb-3">
                Do you want to review the articles before students see them?
              </p>
              {renderYesNo(reviewFirst, setReviewFirst)}
            </div>
          </>
        )}
      </div>
      {step === 3 && error ? <div className="step-error">{error}</div> : null}
    </div>
  );

  const renderPostCreateSuccess = () => {
    if (!isCreateMode) {
      return (
        <div className="post-create-screen success-screen">
          <div className="post-create-copy">
            <h2>{'Your course has been updated successfully.'}</h2>
          </div>
        </div>
      );
    }

    const newsSuggestionsLabel = wantsNewsSuggestions
      ? reviewFirst
        ? "On — you'll review weekly"
        : 'On — auto-published'
      : 'Off';

    return (
      <div className="post-create-screen success-screen">
        <div className="post-create-success-heading">
          <span className="post-create-success-check">
            <Icon icon={ICONS.VERIFIED} />
          </span>
          <h2>You created {name}.</h2>
        </div>
        <div className="post-create-summary-card">
          {[
            { label: 'Course name', value: name },
            {
              label: 'Dates',
              value:
                startDate || endDate
                  ? [
                      startDate ? moment(startDate).format('MMM D') : '',
                      endDate ? moment(endDate).format('MMM D, YYYY') : '',
                    ]
                      .filter(Boolean)
                      .join(' – ')
                  : '—',
            },
            { label: 'Subject', value: normalizedSubject || '—' },
            { label: 'News suggestions', value: newsSuggestionsLabel },
          ].map(({ label, value }) => (
            <div key={label} className="post-create-summary-row">
              <span className="post-create-summary-label">{label}</span>
              <span className="post-create-summary-value">{value}</span>
            </div>
          ))}
        </div>
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div>
            <h3 className="font-weight-600 mt-2">
              Do you want to create your course topics now?
            </h3>
            <div className="ml-3">
              <p className="post-create-topics-intro">Topics enable</p>
              <ul className="post-create-topics-list ml-3">
                <li>Weekly news suggestions tied to your upcoming lectures</li>
                <li>Reading, research, and writing assignments</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="post-create-actions">
          <RectangularButton onClick={onClose}>
            I'll do this later
          </RectangularButton>
          <RectangularButton darkButton onClick={() => openTopicSetupFlow()}>
            Set up topics
          </RectangularButton>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (postCreateScreen === 'success') {
      return renderPostCreateSuccess();
    }

    switch (step) {
      case 0:
        return renderBasicsStep();
      case 1:
        return renderDescriptionStep();
      case 2:
        return renderSubjectStep();
      case 3:
        return renderReadingStep();
      default:
        return null;
    }
  };

  const handlePostCreateAction = () => {
    if (postCreateScreen === 'success') {
      onClose();
    }
  };

  const isShowingPostCreateScreen = !!postCreateScreen;

  return (
    <Modal show className="manage-course-modal">
      <FocusTrap>
        <div>
          <Modal.Body>
            {renderHeader()}
            <div
              className="wizard-shell"
              ref={modalContentRef}
              tabIndex={-1}
              onMouseDown={moveFocusOffInput}
            >
              {!isShowingPostCreateScreen && renderSteps()}
              <div className="wizard-body">{renderBody()}</div>
              {postCreateScreen !== 'success' && (
                <div
                  className={classNames('wizard-footer', {
                    'post-create-footer': isShowingPostCreateScreen,
                  })}
                >
                  <div
                    className="d-flex"
                    style={{ gap: '8px', marginLeft: 'auto' }}
                  >
                    {!isShowingPostCreateScreen && (
                      <RectangularButton
                        onClick={step === 0 ? onClose : goBack}
                      >
                        {step === 0 ? 'Cancel' : 'Back'}
                      </RectangularButton>
                    )}
                    <RectangularButton
                      className="next-button"
                      darkButton
                      disabled={
                        isShowingPostCreateScreen
                          ? false
                          : !isCurrentStepValid() ||
                            saving ||
                            (step === steps.length - 1 && !hasDataChanged)
                      }
                      onClick={
                        isShowingPostCreateScreen
                          ? handlePostCreateAction
                          : handlePrimaryAction
                      }
                    >
                      {isShowingPostCreateScreen
                        ? 'Continue'
                        : step === steps.length - 1
                          ? isCreateMode
                            ? 'Continue'
                            : 'Save'
                          : 'Continue'}
                    </RectangularButton>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
        </div>
      </FocusTrap>
    </Modal>
  );
}

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
    openCreateCourseSourceModal: (courseId) =>
      dispatch(
        Actions.openModal('create-course-source', {
          courseId,
          schoolSubscriptionsMode: true,
        }),
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ManageCourse);
